import React, { useState } from "react"
import { useFormContext } from "react-hook-form"
import { Switch } from "@mantine/core"
import {
    DistrictSelect,
    LowerDistrictSelect,
    StateSelect,
    UpperDistrictSelect,
} from "app/static/frontend/selects/containers/PieSelect"
import { getMessageFieldName } from "@/pages/campaign-form/components/campaigns/writeALetter/Message.helper"
import { CampaignFormValues } from "@/pages/campaign-form/types"
import { useStyles } from "@/pages/campaign-form/components/sections/message/TargetingRestrictions.styles"
import { ACTIVE_CHILD_CAMPAIGN_TYPE_NAME } from "@/pages/campaign-form/constants"

const CAMPAIGN_TYPE_WRITE_EMAIL = 2
const TARGETING_RESTRICTION_STATE = "representing_state"
const TARGETING_RESTRICTION_DISTRICT = "representing_district"
const TARGETING_RESTRICTION_UPPER_DISTRICT = "representing_upper_district"
const TARGETING_RESTRICTION_LOWER_DISTRICT = "representing_lower_district"
const TARGETING_RESTRICTIONS = [
    TARGETING_RESTRICTION_STATE,
    TARGETING_RESTRICTION_DISTRICT,
    TARGETING_RESTRICTION_UPPER_DISTRICT,
    TARGETING_RESTRICTION_LOWER_DISTRICT,
]

interface LocationItem {
    id: number
    name: string
    resource_uri: string
    value: number
}

interface RestrictionItem {
    representing_state?: []
    representing_district?: []
    representing_upper_district?: []
    representing_lower_district?: []
}

const areRestrictionsFilled = (restrictions: RestrictionItem[]): boolean => {
    const result = []
    TARGETING_RESTRICTIONS.forEach((restrictionName: string) => {
        if (
            restrictions.hasOwnProperty(restrictionName) &&
            restrictions[restrictionName] &&
            restrictions[restrictionName].length
        ) {
            result.push(restrictions[restrictionName])
        }
    })
    return !!result.length
}

const getRestrictionsByType = (restrictions: RestrictionItem[], type: string): number[] => {
    const result = []
    if (restrictions.hasOwnProperty(type) && restrictions[type]) {
        restrictions[type].forEach((id: number) => {
            result.push(id)
        })
    }
    return result
}

const collectSelectedRestrictions = (list: LocationItem[]): number[] => {
    const selectedItems = []
    list.forEach((item: LocationItem) => {
        selectedItems.push(item.id)
    })

    return selectedItems
}

export const TargetingRestrictions = ({ index = 0, prefix = null }) => {
    const { classes } = useStyles()
    const formContext = useFormContext<CampaignFormValues>()
    const activeCampaignTypeAction = formContext.getValues(ACTIVE_CHILD_CAMPAIGN_TYPE_NAME)
    let restrictionsKey = getMessageFieldName(index, "restrictions", prefix)

    if (prefix && prefix.includes("multi_action_children")) {
        const campaignType = formContext.getValues(`${prefix}.campaign_type`)

        if (campaignType !== CAMPAIGN_TYPE_WRITE_EMAIL) {
            restrictionsKey = prefix ? `${prefix}.restrictions` : `restrictions`
        }
    } else if (
        (activeCampaignTypeAction === undefined || activeCampaignTypeAction === "undefined") &&
        formContext.getValues("campaign_type") !== CAMPAIGN_TYPE_WRITE_EMAIL
    ) {
        restrictionsKey = prefix ? `${prefix}.restrictions` : `restrictions`
    }

    const restrictionsList = formContext.getValues(restrictionsKey)
    const restrictions = restrictionsList ? restrictionsList : {}
    const [advocateRestrictedValue, setAdvocateRestrictedValue] = useState<boolean>(areRestrictionsFilled(restrictions))
    const [statesValue, setStatesValue] = useState<number[]>(
        getRestrictionsByType(restrictions, TARGETING_RESTRICTION_STATE),
    )
    const [districtsValue, setDistrictsValue] = useState<number[]>(
        getRestrictionsByType(restrictions, TARGETING_RESTRICTION_DISTRICT),
    )
    const [upperDistrictsValue, setUpperDistrictsValue] = useState<number[]>(
        getRestrictionsByType(restrictions, TARGETING_RESTRICTION_UPPER_DISTRICT),
    )
    const [lowerDistrictsValue, setLowerDistrictsValue] = useState<number[]>(
        getRestrictionsByType(restrictions, TARGETING_RESTRICTION_LOWER_DISTRICT),
    )

    const handleTargetingRestriction = (list: LocationItem[], type: string): void => {
        const selectedItems = collectSelectedRestrictions(list)

        switch (type) {
            case TARGETING_RESTRICTION_STATE:
                setStatesValue(selectedItems)
                break
            case TARGETING_RESTRICTION_DISTRICT:
                setDistrictsValue(selectedItems)
                break
            case TARGETING_RESTRICTION_UPPER_DISTRICT:
                setUpperDistrictsValue(selectedItems)
                break
            case TARGETING_RESTRICTION_LOWER_DISTRICT:
                setLowerDistrictsValue(selectedItems)
                break
            default:
                console.error(`The targeting restriction type: ${type} is not supported`)
        }

        restrictions[type] = selectedItems
        formContext.setValue(restrictionsKey, restrictions)
    }

    const resetRestrictions = () => {
        setStatesValue([])
        setDistrictsValue([])
        setUpperDistrictsValue([])
        setLowerDistrictsValue([])
        formContext.setValue(restrictionsKey, {})
    }

    const handleRestrictions = (value) => {
        setAdvocateRestrictedValue(value)
        if (!value) {
            resetRestrictions()
        }
    }

    return (
        <div className={classes.restrictedTargeting}>
            <Switch
                label="Restrict supporter participation"
                description="Elected officials will be restricted by advocates. Custom created Official records will not be restricted by supporter location."
                checked={advocateRestrictedValue}
                onChange={(event) => handleRestrictions(event.target.checked)}
            />
            {advocateRestrictedValue === true && (
                <>
                    <div className={classes.sdgFieldGroup}>
                        <span>States or territories</span>
                        <StateSelect
                            value={statesValue}
                            multi
                            onChange={(value: []) => handleTargetingRestriction(value, TARGETING_RESTRICTION_STATE)}
                        />
                    </div>
                    <div className={classes.sdgFieldGroup}>
                        <span>Congressional districts</span>
                        <DistrictSelect
                            value={districtsValue}
                            multi
                            onChange={(value: []) => handleTargetingRestriction(value, TARGETING_RESTRICTION_DISTRICT)}
                        />
                    </div>
                    <div className={classes.sdgFieldGroup}>
                        <span>Upper state districts</span>
                        <UpperDistrictSelect
                            value={upperDistrictsValue}
                            multi
                            onChange={(value: []) =>
                                handleTargetingRestriction(value, TARGETING_RESTRICTION_UPPER_DISTRICT)
                            }
                        />
                    </div>
                    <div className={classes.sdgFieldGroup}>
                        <span>Lower state districts</span>
                        <LowerDistrictSelect
                            value={lowerDistrictsValue}
                            multi
                            onChange={(value: []) =>
                                handleTargetingRestriction(value, TARGETING_RESTRICTION_LOWER_DISTRICT)
                            }
                        />
                    </div>
                </>
            )}
        </div>
    )
}
